import React from "react";
import {
  Box,
  Container,
  Divider,
  Link,
  Paper,
  Typography,
} from "@mui/material";

const CONTACT_EMAIL = "cynaxia_432@outlook.com";
const LAST_UPDATED = "18 de abril de 2026";

/**
 * Política de privacidad y avisos legales para publicación (incl. requisitos típicos de Google Play).
 * El texto es informativo; conviene revisión por asesor legal antes de procesos formales.
 */
export default function PrivacyPolicy() {
  return (
    <Container maxWidth="md" sx={{ py: 3, pb: 6 }}>
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, bgcolor: "background.paper" }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: "#0f1a56", fontWeight: 700 }}>
          Política de privacidad y términos informativos
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Última actualización: {LAST_UPDATED}
        </Typography>

        <Section title="1. Identificación de la aplicación">
          <Body>
            La aplicación web «dosis_Utiles» (en adelante, la «App») es una herramienta de consulta y
            apoyo al estudio o la práctica clínica, desarrollada por Medstudio. La App ofrece acceso a
            información de medicamentos, criterios, protocolos, guías y material educativo, según lo
            disponible en cada sección.
          </Body>
        </Section>

        <Section title="2. Naturaleza de la información — no es asesoramiento médico">
          <Body>
            El contenido de la App tiene carácter meramente informativo y de apoyo. No constituye
            diagnóstico, tratamiento, prescripción ni recomendación médica personalizada. La
            información no sustituye en ningún caso el juicio clínico, la evaluación presencial ni las
            indicaciones de un médico u otro profesional de la salud debidamente habilitado.
          </Body>
          <Body>
            Usted es el único responsable del uso que haga de la App y de las decisiones que adopte con
            base en ella. Los titulares de la App no se hacen responsables por el uso indebido,
            interpretaciones erróneas, omisiones, errores de transcripción o por decisiones clínicas
            tomadas sin la supervisión adecuada. En caso de urgencia o duda, consulte siempre a un
            profesional de la salud o a los servicios de emergencia.
          </Body>
        </Section>

        <Section title="3. Responsable del tratamiento de datos personales">
          <Body>
            Para efectos de esta política, el responsable del tratamiento de los datos personales
            asociados al funcionamiento de la App es el titular del proyecto en coordinación con
            Medstudio. Para ejercer sus derechos o realizar consultas sobre privacidad puede
            contactarnos en:{" "}
            <Link href={`mailto:${CONTACT_EMAIL}`} color="primary">
              {CONTACT_EMAIL}
            </Link>
            .
          </Body>
        </Section>

        <Section title="4. Datos que podemos tratar">
          <Body>
            Según cómo utilice la App, podemos tratar las siguientes categorías de datos:
          </Body>
          <BulletList
            items={[
              "Datos de cuenta (opcional): si inicia sesión con Google, podemos recibir identificador de usuario, nombre, correo electrónico e imagen de perfil, según lo permita el proveedor de inicio de sesión.",
              "Identificador de sesión: si la App utiliza acceso anónimo o técnico para funciones como notas personales, se puede generar un identificador de usuario técnico (UID) asociado a su dispositivo o navegador.",
              "Contenido que usted introduce: por ejemplo, texto almacenado en la función de notas personales, asociado a su identificador de usuario en nuestro backend.",
              "Datos técnicos y de uso: dirección IP, tipo de navegador, identificadores de dispositivo o registros necesarios para seguridad, funcionamiento y mejora del servicio, incluidos los que puedan procesar nuestros proveedores de infraestructura (por ejemplo, Google/Firebase).",
            ]}
          />
        </Section>

        <Section title="5. Finalidades del tratamiento">
          <BulletList
            items={[
              "Permitir el inicio de sesión y la gestión de la cuenta cuando corresponda.",
              "Almacenar y sincronizar las notas u otras funciones que dependan de un identificador de usuario.",
              "Mantener la seguridad de la App, prevenir abusos y cumplir obligaciones legales aplicables.",
              "Mejorar la experiencia de uso y la estabilidad del servicio.",
            ]}
          />
        </Section>

        <Section title="6. Base legal">
          <Body>
            El tratamiento se basa en la ejecución de medidas precontractuales y del contrato de uso de
            la App cuando sea aplicable, en el interés legítimo de operar y asegurar el servicio, y en
            su consentimiento cuando la ley lo exija (por ejemplo, para ciertas cookies o comunicaciones
            opcionales, si se implementan).
          </Body>
        </Section>

        <Section title="7. Encargados y servicios de terceros (Google / Firebase)">
          <Body>
            Utilizamos servicios de Google, incluidos Firebase Authentication y Cloud Firestore, para
            autenticación y almacenamiento. El tratamiento de datos por parte de Google se rige por
            sus propias políticas. Le recomendamos revisar:
          </Body>
          <Box component="ul" sx={{ pl: 2.5, mt: 1, mb: 0 }}>
            <li>
              <Link
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Política de privacidad de Google
              </Link>
            </li>
            <li>
              <Link
                href="https://firebase.google.com/support/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacidad y seguridad en Firebase
              </Link>
            </li>
          </Box>
          <Body sx={{ mt: 2 }}>
            Estos enlaces son relevantes para cumplir con la transparencia exigida en tiendas de
            aplicaciones (incluida Google Play) respecto de proveedores que puedan acceder o procesar
            datos en nuestro nombre.
          </Body>
        </Section>

        <Section title="8. Conservación">
          <Body>
            Conservamos los datos el tiempo necesario para las finalidades descritas y para cumplir
            obligaciones legales. Las notas u otros contenidos asociados a su cuenta permanecerán
            mientras mantenga la cuenta o la funcionalidad activa, salvo que solicite su eliminación
            conforme a la sección de derechos.
          </Body>
        </Section>

        <Section title="9. Seguridad">
          <Body>
            Aplicamos medidas técnicas y organizativas razonables para proteger la información frente a
            accesos no autorizados, pérdida o alteración. Ningún sistema es 100% seguro; le pedimos que
            proteja sus credenciales y dispositivos.
          </Body>
        </Section>

        <Section title="10. Sus derechos">
          <Body>
            Puede solicitar acceso, rectificación, supresión, limitación u oposición al tratamiento de
            sus datos personales, según corresponda en su jurisdicción (por ejemplo, derechos previstos
            en normativa de protección de datos aplicable). Para ejercerlos, escriba a{" "}
            <Link href={`mailto:${CONTACT_EMAIL}`} color="primary">
              {CONTACT_EMAIL}
            </Link>{" "}
            indicando su solicitud y un medio de contacto. Responderemos en un plazo razonable.
          </Body>
        </Section>

        <Section title="11. Menores de edad">
          <Body>
            La App no está dirigida a menores de 13 años (o de 16 años en el Espacio Económico Europeo,
            cuando aplique la normativa local). No recopilamos a sabiendas datos de menores de forma
            intencionada. Si considera que se han recopilado datos de un menor, contáctenos para su
            eliminación.
          </Body>
        </Section>

        <Section title="12. Transferencias internacionales">
          <Body>
            Los servicios de Google/Firebase pueden implicar el tratamiento de datos en servidores
            ubicados fuera de su país. Dichas transferencias pueden ampararse en cláusulas contractuales
            tipo u otros mecanismos reconocidos por la normativa aplicable.
          </Body>
        </Section>

        <Section title="13. Cumplimiento con Google Play y transparencia «Data safety»">
          <Body>
            Si la App se distribuye a través de Google Play, el titular debe completar en la Consola de
            Play la sección de seguridad de datos («Data safety») de forma coherente con esta política:
            tipos de datos recopilados, finalidades, si los datos se comparten, prácticas de seguridad y
            si el usuario puede solicitar eliminación. Esta política debe coincidir con lo declarado en
            la ficha de la aplicación. Google puede actualizar sus requisitos; el desarrollador es
            responsable de mantener la información actualizada.
          </Body>
          <Body>
            Asimismo, Google Play exige que las aplicaciones cumplan las políticas del programa para
            desarrolladores, incluidas las relativas a contenido, salud y privacidad. Usted reconoce
            que el incumplimiento de dichas políticas puede conllevar la retirada de la App de la
            tienda.
          </Body>
        </Section>

        <Section title="14. Cambios en esta política">
          <Body>
            Podemos modificar esta política para reflejar cambios legales o en el servicio. Publicaremos
            la versión actualizada en la App con una nueva fecha de «Última actualización». El uso
            continuado tras los cambios puede implicar la aceptación de la política revisada, salvo que
            la ley exija un procedimiento distinto.
          </Body>
        </Section>

        <Section title="15. Contacto">
          <Body>
            Consultas sobre privacidad, datos personales o esta política:{" "}
            <Link href={`mailto:${CONTACT_EMAIL}`} color="primary">
              {CONTACT_EMAIL}
            </Link>
            .
          </Body>
        </Section>

        <Divider sx={{ my: 3 }} />

        <Typography variant="body2" color="text.secondary" component="p">
          El presente documento tiene fines de transparencia y cumplimiento de buenas prácticas. No
          sustituye asesoría jurídica; para situaciones concretas (litigios, contratos, cumplimiento
          sector salud) se recomienda consultar a un abogado.
        </Typography>
      </Paper>
    </Container>
  );
}

function Section({ title, children }) {
  return (
    <Box component="section" sx={{ mb: 3 }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ color: "#0f1a56", fontWeight: 600 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function Body({ children, sx }) {
  return (
    <Typography variant="body1" component="p" sx={{ mb: 1.5, lineHeight: 1.7, ...sx }}>
      {children}
    </Typography>
  );
}

function BulletList({ items }) {
  return (
    <Box component="ul" sx={{ pl: 2.5, my: 0 }}>
      {items.map((text, i) => (
        <Typography
          key={`privacy-li-${i}`}
          component="li"
          variant="body1"
          sx={{ mb: 1, lineHeight: 1.7 }}
        >
          {text}
        </Typography>
      ))}
    </Box>
  );
}
